import type { TakeDownPiece, TakeDownResult } from '@/lib/api/generated/model';

/**
 * What a take-down of the set says (spec 213; service spec 167).
 *
 * Taking a shared item down can take the linked pieces the same person shared
 * with it, as long as nothing else in the Library uses them. These are the
 * sentences that offer them and report what went, kept pure so the counts and
 * the singulars are tested without a drawer.
 */

const NOUNS = {
  workflow: ['linked workflow', 'linked workflows'],
  agent: ['agent', 'agents'],
  skill: ['skill', 'skills'],
} as const;

function counted(n: number, [one, many]: readonly [string, string]): string {
  return `${String(n)} ${n === 1 ? one : many}`;
}

/** "a", "a and b", "a, b and c". */
function listed(parts: readonly string[]): string {
  if (parts.length <= 1) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} and ${parts.at(-1) ?? ''}`;
}

/**
 * "Also take down 2 linked workflows and 6 agents you shared". ``sharer`` is
 * whoever shared the item, or ``null`` when that is the person reading.
 */
export function alsoLine(
  pieces: readonly TakeDownPiece[],
  sharer: string | null,
): string {
  const parts = (['workflow', 'agent', 'skill'] as const)
    .map(
      (kind) => [kind, pieces.filter((p) => p.kind === kind).length] as const,
    )
    .filter(([, n]) => n > 0)
    .map(([kind, n]) => counted(n, NOUNS[kind]));
  return `Also take down ${listed(parts)} ${sharer ?? 'you'} shared`;
}

/** "Took down Epic delivery and 8 linked pieces.", or just the item when it went alone. */
export function takenDownMessage(result: TakeDownResult): string {
  const item = result.taken_down.at(-1);
  const name = item?.name ?? 'it';
  const others = result.taken_down.length - 1;
  return others > 0
    ? `Took down ${name} and ${counted(others, ['linked piece', 'linked pieces'])}.`
    : `Took down ${name}.`;
}

/** The folded list's heading. */
export function stayingLabel(count: number): string {
  return `Stays in the Library (${String(count)})`;
}
